import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../api";
import { StatusBadge } from "../components/StatusBadge";
import useTenantPath from "../lib/useTenantPath";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowUp, ArrowDown, ArrowDownToLine, X, UserPlus, Heart, SkipForward, PhoneCall, Zap, Clock, Pause, Play, LogOut, PowerOff, Tv, Volume2 } from "lucide-react";
import PauseReasonDialog from "../components/PauseReasonDialog";
import { useAuth } from "../context/AuthContext";

const REASON_LABELS = {
  banheiro: "Banheiro",
  almoco: "Almoço",
  procedimento: "Procedimento particular",
  outro: "Outro",
  sem_resposta: "Sem resposta",
};

function CountdownBadge({ timeoutAt, serverTime }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  const target = new Date(timeoutAt).getTime();
  const anchor = serverTime ? new Date(serverTime).getTime() : Date.now();
  const drift = anchor - Date.now();
  const remaining = Math.max(0, Math.floor((target - now - drift) / 1000));
  return (
    <span data-testid="call-countdown" className="inline-flex items-center gap-1 rounded-full bg-black/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
      <Clock size={10} /> {remaining}s
    </span>
  );
}

export default function ReceptionDashboard() {
  const { user } = useAuth();
  const isMaster = user?.admin_level === "master";
  const nav = useNavigate();
  const tp = useTenantPath();
  const [state, setState] = useState({ categories: [], professionals: [], stats: {}, server_time: null });
  const [confirm, setConfirm] = useState(null);
  const [prefDialog, setPrefDialog] = useState(null);
  const [addDialog, setAddDialog] = useState(null);
  const [prefProf, setPrefProf] = useState("");
  const [addProf, setAddProf] = useState("");
  const [pauseTarget, setPauseTarget] = useState(null); // {prof} or null
  const [allowReplay, setAllowReplay] = useState(true);
  const [statModal, setStatModal] = useState(null); // 'working' | 'available' | 'serving' | null
  const seenResponsesRef = useRef(new Set());

  useEffect(() => {
    api.get("/settings/calls").then(({ data }) => {
      setAllowReplay(data?.allow_manual_replay !== false);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      // detect new declined/timeout responses to show toast
      data.categories.forEach((c) => {
        const r = c.recent_response;
        if (r && !seenResponsesRef.current.has(r.id)) {
          seenResponsesRef.current.add(r.id);
          const prof = data.professionals.find((p) => p.id === r.professional_id);
          const label = REASON_LABELS[r.reason || "sem_resposta"] || "Sem resposta";
          if (r.status === "declined") {
            toast(`${prof?.name || "Profissional"} passou a vez — ${label}`, {
              description: `${c.name}${r.note ? " · " + r.note : ""}`,
              duration: 6000,
            });
          } else if (r.status === "timeout") {
            toast.warning(`${prof?.name || "Profissional"} não respondeu à chamada`, {
              description: `${c.name} · 120s sem resposta`,
              duration: 6000,
            });
          }
        }
      });
      setState(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[fila] poll error", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const doAction = async (fn, successMsg) => {
    try {
      await fn();
      await load();
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const callProf = (category, prof) =>
    doAction(
      () => api.post("/queue/call", { category_id: category.id, professional_id: prof.id }),
      `Chamando ${prof.name}…`,
    );

  const replayCall = (call) =>
    doAction(() => api.post(`/calls/${call.id}/replay`), "Chamada repetida na TV");

  const cancelCall = (call) =>
    doAction(() => api.post(`/calls/${call.id}/cancel`), "Chamada cancelada");

  const attendNow = (category, prof, type = "vez") =>
    setConfirm({
      title: type === "vez" ? "Atender agora" : "Atendimento por preferência",
      message:
        type === "vez"
          ? `Iniciar atendimento imediato para ${prof.name} sem esperar resposta?`
          : `Confirmar atendimento POR PREFERÊNCIA para ${prof.name}? A posição na fila não será alterada.`,
      cta: "Confirmar",
      testid: "confirm-attend",
      onConfirm: () =>
        doAction(
          () => api.post("/queue/attend", { category_id: category.id, professional_id: prof.id, type }),
          `${prof.name} em atendimento`,
        ),
    });

  const passTurnAdmin = (category, prof) =>
    setConfirm({
      title: "Passar a vez",
      message: `Passar ${prof.name} para o final da fila de ${category.name}?`,
      cta: "Passar vez",
      testid: "confirm-pass-turn",
      onConfirm: () =>
        doAction(
          () => api.post("/queue/pass-turn", { category_id: category.id, professional_id: prof.id, reason: "sem_resposta" }),
          "Vez passada",
        ),
    });

  const finish = (prof) =>
    setConfirm({
      title: "Finalizar atendimento",
      message: `Finalizar o atendimento de ${prof.name}?`,
      cta: "Finalizar",
      testid: "confirm-finish",
      onConfirm: () =>
        doAction(() => api.post("/queue/finish", { attendance_id: prof.current_attendance_id }), "Atendimento finalizado"),
    });

  const move = (category, prof, action) =>
    doAction(() => api.post("/queue/move", { category_id: category.id, professional_id: prof.id, action }));

  const submitPause = async ({ reason, note }) => {
    if (!pauseTarget) return;
    const prof = pauseTarget.prof;
    try {
      const { data } = await api.post(`/professionals/${prof.id}/pause`, { reason, note });
      toast.success(data?.left_queues ? `${prof.name} foi para o almoço` : `${prof.name} pausada`);
      setPauseTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resumeProf = (prof) =>
    doAction(() => api.post(`/professionals/${prof.id}/resume`), `${prof.name} voltou`);

  const endShiftFor = (prof) => {
    if (!window.confirm(`Encerrar o expediente de ${prof.name}?`)) return;
    doAction(() => api.post(`/professionals/${prof.id}/end-shift`), `Expediente de ${prof.name} encerrado`);
  };

  const endAllShifts = async () => {
    if (!window.confirm("Encerrar o expediente de TODAS as profissionais que ainda estão trabalhando?")) return;
    try {
      const { data } = await api.post("/admin/end-all-shifts");
      toast.success(`Encerrados: ${data.ended || 0} expediente(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPref = (category) => {
    setPrefProf("");
    setPrefDialog({ category });
  };
  const openAdd = (category) => {
    setAddProf("");
    setAddDialog({ category });
  };
  const submitPref = () => {
    if (!prefProf) return;
    const prof = state.professionals.find((p) => p.id === prefProf);
    const category = prefDialog.category;
    setPrefDialog(null);
    doAction(
      () => api.post("/queue/call", { category_id: category.id, professional_id: prof.id, type: "preferencia" }),
      `Chamando ${prof.name} (preferência)…`,
    );
  };
  const submitAdd = () => {
    if (!addProf) return;
    doAction(
      () => api.post("/queue/move", { category_id: addDialog.category.id, professional_id: addProf, action: "add" }),
      "Adicionado à fila",
    );
    setAddDialog(null);
  };

  const availableForPref = (category) =>
    state.professionals.filter(
      (p) => p.status === "available" && (p.category_ids || []).includes(category.id),
    );
  const availableToAdd = (category) =>
    state.professionals.filter(
      (p) => p.active && (p.category_ids || []).includes(category.id) && !category.queue.find((q) => q.id === p.id),
    );

  const activeCategories = useMemo(
    () => state.categories.filter((c) => {
      if (!c.active) return false;
      if (!c.hide_when_empty) return true;
      const hasQueue = (c.queue || []).length > 0;
      const hasWorking = (state.professionals || []).some(
        (p) => (p.category_ids || []).includes(c.id) && p.status && p.status !== "off_duty",
      );
      return hasQueue || hasWorking;
    }),
    [state.categories, state.professionals],
  );

  return (
    <div>
      <div className="mb-8 flex flex-col gap-2">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gray-500">Dashboard</div>
            <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">Fila do dia</h1>
          </div>
          {isMaster && (
            <div className="flex flex-wrap items-center gap-2">
              <a
                data-testid="btn-open-tv"
                href="/tv"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white hover:-translate-y-[1px] transition-transform"
              >
                <Tv size={14} /> Abrir Painel TV
              </a>
              <button
                data-testid="btn-end-all-shifts"
                onClick={endAllShifts}
                className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-widest text-red-600 hover:bg-red-50"
              >
                <PowerOff size={14} /> Encerrar dia (todos)
              </button>
            </div>
          )}
          {!isMaster && (
            <a
              data-testid="btn-open-tv"
              href="/tv"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-xs font-semibold uppercase tracking-widest text-white hover:-translate-y-[1px] transition-transform"
            >
              <Tv size={14} /> Abrir Painel TV
            </a>
          )}
        </div>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        <Stat label="Trabalhando" value={state.stats?.working ?? 0} testid="stat-working" onClick={() => setStatModal("working")} />
        <Stat label="Disponíveis" value={state.stats?.available ?? 0} testid="stat-available" color="#10B981" onClick={() => setStatModal("available")} />
        <Stat label="Atendendo" value={state.stats?.serving ?? 0} testid="stat-serving" color="#EF4444" onClick={() => setStatModal("serving")} />
        <Stat label="Atendimentos hoje" value={state.stats?.attendances_today ?? 0} testid="stat-att-today" onClick={() => nav(tp("/relatorios"))} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        {activeCategories.map((cat) => {
          const pendingCall = cat.pending_call;
          const pendingProf = pendingCall
            ? state.professionals.find((p) => p.id === pendingCall.professional_id)
            : null;
          return (
            <div
              key={cat.id}
              data-testid={`category-card-${cat.name.toLowerCase()}`}
              className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
            >
              <div className="mb-4 flex items-start justify-between gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-gray-500">Categoria</div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">{cat.name}</h3>
                  <span
                    data-testid={`cat-mode-badge-${cat.name.toLowerCase()}`}
                    className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                      (cat.queue_mode || "vez") === "quantidade"
                        ? "bg-pink-100 text-pink-700"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {(cat.queue_mode || "vez") === "quantidade" ? "Por quantidade" : "Por vez"}
                  </span>
                </div>
                <div className="text-right text-xs text-gray-400">{cat.queue.length} na fila</div>
              </div>

              {pendingCall && pendingProf && (
                <div data-testid={`pending-call-${cat.name.toLowerCase()}`} className="mb-3 rounded-xl border-2 border-black bg-pink-50/70 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <PhoneCall size={14} className="text-black" />
                      <div className="text-sm">
                        <div className="font-medium">Aguardando {pendingProf.name}</div>
                        <div className="text-[11px] text-gray-600">Chamada enviada</div>
                      </div>
                    </div>
                    <CountdownBadge timeoutAt={pendingCall.timeout_at} serverTime={state.server_time} />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      data-testid={`btn-cancel-call-${cat.name.toLowerCase()}`}
                      onClick={() => cancelCall(pendingCall)}
                      className="rounded-full border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancelar chamada
                    </button>
                    {allowReplay && (
                      <button
                        data-testid={`btn-replay-call-${cat.name.toLowerCase()}`}
                        onClick={() => replayCall(pendingCall)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-pink-400 bg-pink-100 px-3 py-1 text-xs font-semibold text-pink-800 hover:bg-pink-200"
                        title="Reexibir e refalar a chamada sem alterar timeout"
                      >
                        <Volume2 size={12} /> Repetir chamada
                      </button>
                    )}
                    <button
                      data-testid={`btn-force-attend-${cat.name.toLowerCase()}`}
                      onClick={() => attendNow(cat, pendingProf, "vez")}
                      className="rounded-full bg-black px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white"
                    >
                      Atender agora
                    </button>
                  </div>
                </div>
              )}

              {cat.queue.length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400">
                  Nenhum profissional na fila
                </div>
              )}

              <ul className="space-y-2">
                {cat.queue.map((prof, idx) => {
                  const isNext = prof.id === cat.na_vez_id;
                  const hasPending = pendingCall?.professional_id === prof.id;
                  return (
                    <li
                      key={prof.id}
                      data-testid={`queue-item-${cat.name.toLowerCase()}-${idx}`}
                      className={`rounded-xl border p-3 transition-colors ${
                        isNext ? "border-black bg-pink-50/60" : "border-gray-100 bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 font-display text-sm font-semibold">
                            {idx + 1}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate font-medium">
                              {prof.name}
                              {cat.queue_mode === "quantidade" && (
                                <span data-testid={`att-count-${cat.name.toLowerCase()}-${prof.id}`} className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-800">
                                  {prof.attendance_count ?? 0} atend.
                                </span>
                              )}
                            </div>
                            <div className="mt-1"><StatusBadge status={prof.status} pause_reason={prof.pause_reason} /></div>
                          </div>
                        </div>
                        {isNext && (
                          <span
                            data-testid={`na-vez-${cat.name.toLowerCase()}`}
                            className="na-vez-badge rounded-full bg-black px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white"
                          >
                            Na vez
                          </span>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {isNext && prof.status === "available" && !hasPending && (
                          <>
                            <button
                              data-testid={`btn-chamar-${cat.name.toLowerCase()}`}
                              onClick={() => callProf(cat, prof)}
                              className="inline-flex items-center gap-1.5 rounded-full bg-black px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white transition-transform hover:-translate-y-[1px]"
                            >
                              <PhoneCall size={12} /> Chamar
                            </button>
                            <button
                              data-testid={`btn-atender-agora-${cat.name.toLowerCase()}`}
                              onClick={() => attendNow(cat, prof, "vez")}
                              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
                            >
                              <Zap size={12} /> Atender agora
                            </button>
                            <button
                              data-testid={`btn-passar-vez-${cat.name.toLowerCase()}`}
                              onClick={() => passTurnAdmin(cat, prof)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                            >
                              <SkipForward size={12} /> Passar vez
                            </button>
                          </>
                        )}
                        {prof.status === "serving" && prof.current_category_id === cat.id && (
                          <button
                            data-testid={`btn-finalizar-${cat.name.toLowerCase()}-${prof.id}`}
                            onClick={() => finish(prof)}
                            className="rounded-full bg-red-500 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-red-600"
                          >
                            Finalizar
                          </button>
                        )}
                        <div className="ml-auto flex items-center gap-1">
                          {prof.status === "available" && (
                            <IconBtn onClick={() => setPauseTarget({ prof })} testid={`btn-pause-${cat.name.toLowerCase()}-${prof.id}`} title="Pausar profissional">
                              <Pause size={14} />
                            </IconBtn>
                          )}
                          {prof.status === "paused" && (
                            <IconBtn onClick={() => resumeProf(prof)} testid={`btn-resume-${cat.name.toLowerCase()}-${prof.id}`} title="Voltar a ficar disponível">
                              <Play size={14} />
                            </IconBtn>
                          )}
                          {isMaster && (
                            <>
                              <IconBtn onClick={() => move(cat, prof, "up")} disabled={idx === 0} testid={`btn-up-${cat.name.toLowerCase()}-${idx}`} title="Subir">
                                <ArrowUp size={14} />
                              </IconBtn>
                              <IconBtn onClick={() => move(cat, prof, "down")} disabled={idx === cat.queue.length - 1} testid={`btn-down-${cat.name.toLowerCase()}-${idx}`} title="Descer">
                                <ArrowDown size={14} />
                              </IconBtn>
                              <IconBtn onClick={() => move(cat, prof, "end")} disabled={idx === cat.queue.length - 1} testid={`btn-end-${cat.name.toLowerCase()}-${idx}`} title="Para o final">
                                <ArrowDownToLine size={14} />
                              </IconBtn>
                              <IconBtn onClick={() => move(cat, prof, "remove")} testid={`btn-remove-${cat.name.toLowerCase()}-${idx}`} title="Retirar da fila" danger>
                                <X size={14} />
                              </IconBtn>
                            </>
                          )}
                          <IconBtn
                            onClick={() => endShiftFor(prof)}
                            testid={`btn-end-shift-${cat.name.toLowerCase()}-${prof.id}`}
                            title="Encerrar expediente"
                            danger
                          >
                            <LogOut size={14} />
                          </IconBtn>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
                <button
                  data-testid={`btn-preferencia-${cat.name.toLowerCase()}`}
                  onClick={() => openPref(cat)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-pink-50"
                >
                  <Heart size={12} /> Atendimento por preferência
                </button>
                {isMaster && (
                  <button
                    data-testid={`btn-add-to-queue-${cat.name.toLowerCase()}`}
                    onClick={() => openAdd(cat)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50"
                  >
                    <UserPlus size={12} /> Adicionar à fila
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid={confirm?.testid}>
          <DialogHeader>
            <DialogTitle className="font-display">{confirm?.title}</DialogTitle>
            <DialogDescription>{confirm?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              onClick={() => setConfirm(null)}
              className="rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              data-testid="btn-cancel-confirm"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                const fn = confirm.onConfirm;
                setConfirm(null);
                fn();
              }}
              className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:-translate-y-[1px] transition-transform"
              data-testid="btn-confirm-confirm"
            >
              {confirm?.cta || "Confirmar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!prefDialog} onOpenChange={(o) => !o && setPrefDialog(null)}>
        <DialogContent data-testid="dialog-preferencia">
          <DialogHeader>
            <DialogTitle className="font-display">Atendimento por preferência</DialogTitle>
            <DialogDescription>
              Selecione a profissional pedida pelo cliente em {prefDialog?.category.name}. A posição normal na fila não será alterada.
            </DialogDescription>
          </DialogHeader>
          <Select value={prefProf} onValueChange={setPrefProf}>
            <SelectTrigger data-testid="select-preferencia-prof">
              <SelectValue placeholder="Escolha a profissional" />
            </SelectTrigger>
            <SelectContent>
              {prefDialog && availableForPref(prefDialog.category).map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`opt-pref-${p.id}`}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter className="gap-2">
            <button onClick={() => setPrefDialog(null)} className="rounded-full border border-gray-200 px-4 py-2 text-sm">Cancelar</button>
            <button onClick={submitPref} disabled={!prefProf} className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="btn-submit-preferencia">
              Confirmar preferência
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!addDialog} onOpenChange={(o) => !o && setAddDialog(null)}>
        <DialogContent data-testid="dialog-add-queue">
          <DialogHeader>
            <DialogTitle className="font-display">Adicionar à fila</DialogTitle>
            <DialogDescription>Selecione uma profissional habilitada em {addDialog?.category.name}.</DialogDescription>
          </DialogHeader>
          <Select value={addProf} onValueChange={setAddProf}>
            <SelectTrigger data-testid="select-add-prof">
              <SelectValue placeholder="Escolha a profissional" />
            </SelectTrigger>
            <SelectContent>
              {addDialog && availableToAdd(addDialog.category).map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`opt-add-${p.id}`}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter className="gap-2">
            <button onClick={() => setAddDialog(null)} className="rounded-full border border-gray-200 px-4 py-2 text-sm">Cancelar</button>
            <button onClick={submitAdd} disabled={!addProf} className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="btn-submit-add-queue">
              Adicionar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PauseReasonDialog
        open={!!pauseTarget}
        onOpenChange={(o) => !o && setPauseTarget(null)}
        onConfirm={submitPause}
        title={pauseTarget ? `Pausar ${pauseTarget.prof.name}` : "Pausar"}
        description="Escolha o motivo. Se for Almoço, a profissional sai das filas até voltar."
        testidPrefix="admin-pause"
      />

      <StatModal
        open={statModal === "working"}
        onClose={() => setStatModal(null)}
        title="Profissionais trabalhando"
        emptyText="Nenhum profissional trabalhando no momento."
        testid="stat-modal-working"
        profs={state.professionals.filter((p) => p.status && ["available", "serving", "paused"].includes(p.status))}
        categories={state.categories}
      />
      <StatModal
        open={statModal === "available"}
        onClose={() => setStatModal(null)}
        title="Profissionais disponíveis"
        emptyText="Nenhum profissional disponível no momento."
        testid="stat-modal-available"
        profs={state.professionals.filter((p) => p.status === "available")}
        categories={state.categories}
      />
      <StatModal
        open={statModal === "serving"}
        onClose={() => setStatModal(null)}
        title="Profissionais em atendimento"
        emptyText="Nenhum profissional em atendimento no momento."
        testid="stat-modal-serving"
        profs={state.professionals.filter((p) => p.status === "serving")}
        categories={state.categories}
      />
    </div>
  );
}

function StatModal({ open, onClose, title, emptyText, testid, profs, categories }) {
  const catName = (id) => (categories.find((c) => c.id === id) || {}).name || "—";
  const profCats = (p) => {
    const ids = p.category_ids || [];
    if (ids.length === 0) return "—";
    return ids.map(catName).filter(Boolean).join(" • ");
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testid} className="max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>
            {profs.length} profissional{profs.length === 1 ? "" : "is"}.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto -mx-6 px-6 flex-1">
          {profs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-400">
              {emptyText}
            </div>
          ) : (
            <ul className="space-y-2">
              {profs.map((p) => (
                <li
                  key={p.id}
                  data-testid={`${testid}-item-${p.id}`}
                  className="rounded-xl border border-gray-100 bg-white p-3 flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="mt-1 text-xs text-gray-500 truncate">{profCats(p)}</div>
                  </div>
                  <StatusBadge status={p.status} pause_reason={p.pause_reason} />
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <button
            data-testid={`${testid}-close`}
            onClick={onClose}
            className="rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Fechar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, testid, color, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`text-left rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 transition-all ${
        clickable ? "cursor-pointer hover:border-pink-200 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-300" : "cursor-default"
      }`}
    >
      <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-display text-3xl font-semibold tracking-tight" style={{ color: color || "#0A0A0A" }}>
          {value}
        </div>
      </div>
    </button>
  );
}

function IconBtn({ children, onClick, disabled, testid, title, danger }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`rounded-full border border-gray-200 p-1.5 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed ${
        danger ? "hover:bg-red-50 hover:text-red-600" : ""
      }`}
    >
      {children}
    </button>
  );
}
